import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ArrowLeft, Gem, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { diamondsService } from '@/services/diamonds.service';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const IMAGE_BASE_URL = 'https://xdoinfinitto.com.br:3000';

const formatDate = (dateString: string): string => {
  try {
    if (!dateString) return '-';
    return format(new Date(dateString), "dd/MM/yyyy - HH:mm", { locale: ptBR });
  } catch {
    return '-';
  }
};

interface ItemImage {
  fileUploaded: string;
  base64: string | null;
  fileType: string | null;
}

interface ItemDetail {
  id: number;
  nome: string;
  descricao: string;
  imagem: string;
  valor: number;
  custo: number;
  qntd: number;
  qntd_disponivel: number;
  qntd_resgatada: number;
  ativo: boolean;
  categoria_id: number | null;
  categoria_nome: string | null;
  date_created: string;
  date_updated: string | null;
  date_start: string | null;
  date_end: string | null;
  images: ItemImage[];
}

export default function ResgateItemDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (id && id !== 'null' && id !== 'undefined') {
      loadItem(Number(id));
    } else {
      setIsLoading(false);
      setLoadError(true);
    }
  }, [id]);

  const loadItem = async (itemId: number) => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const response: any = await diamondsService.getDetailItem(itemId);
      if (response && response.data) {
        setItem(response.data);
      } else {
        setLoadError(true);
        toast.error('Item não encontrado');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do item:', error);
      setLoadError(true);
      toast.error('Erro ao buscar detalhes do item');
    } finally {
      setIsLoading(false);
    }
  };

  const getImageUrl = (image: ItemImage): string => {
    return `${IMAGE_BASE_URL}/diamonds/item-image/${image.fileUploaded}`;
  };

  const nextImage = () => {
    if (item && item.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevImage = () => {
    if (item && item.images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold">Aguarde</h2>
          <p className="text-muted-foreground">Carregando detalhes do item...</p>
        </div>
      </div>
    );
  }

  if (!item || loadError) {
    return (
      <div className="p-6 text-center">
        <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">
          {loadError ? 'Não foi possível carregar os detalhes deste item.' : 'Item não encontrado.'}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/relatorios-resgates')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para resgates
        </Button>
      </div>
    );
  }

  const totalImages = item.images?.length || 0;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Card>
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold">{item.nome}</h2>
              <p className="text-sm text-muted-foreground">ID: {item.id}</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/relatorios-resgates')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para resgates
            </Button>
          </div>

          <hr className="my-4" />

          {/* Imagem + Infos principais */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-6">
            {/* Galeria de imagens */}
            <div className="flex flex-col items-center">
              <div className="relative w-full max-w-[400px] aspect-square rounded-lg border overflow-hidden bg-muted">
                <img
                  src={totalImages > 0 ? getImageUrl(item.images[currentImageIndex]) : `${IMAGE_BASE_URL}/diamonds/item-image/${item.imagem}`}
                  alt={item.nome}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    if (!img.src.endsWith('/placeholder.svg')) {
                      img.src = '/placeholder.svg';
                    }
                  }}
                />
                {totalImages > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-colors"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-colors"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}
              </div>
              {/* Thumbnails */}
              {totalImages > 1 && (
                <div className="flex gap-2 mt-3 flex-wrap justify-center">
                  {item.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={cn(
                        "w-14 h-14 rounded-md border-2 overflow-hidden transition-all",
                        idx === currentImageIndex ? "border-primary ring-2 ring-primary/30" : "border-muted hover:border-muted-foreground/50"
                      )}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${item.nome} ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const imgEl = e.target as HTMLImageElement;
                          if (!imgEl.src.endsWith('/placeholder.svg')) {
                            imgEl.src = '/placeholder.svg';
                          }
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
              {totalImages > 1 && (
                <span className="text-xs text-muted-foreground mt-2">
                  {currentImageIndex + 1} / {totalImages}
                </span>
              )}
            </div>

            {/* Informações do item */}
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Status:</span>
                <Badge className={item.ativo ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                  {item.ativo ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>

              {/* Categoria */}
              {item.categoria_nome && (
                <div>
                  <span className="text-sm font-semibold">Categoria:</span>
                  <p className="mt-1">{item.categoria_nome}</p>
                </div>
              )}

              {/* Valor em diamantes */}
              <div>
                <span className="text-sm font-semibold">Valor (Diamantes):</span>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-2xl font-bold text-purple-600">{item.valor}</span>
                  <Gem className="h-6 w-6 text-purple-600" />
                </div>
              </div>

              {/* Custo */}
              <div>
                <span className="text-sm font-semibold">Custo (R$):</span>
                <p className="mt-1 text-lg font-medium">
                  {item.custo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>
              </div>

              {/* Descrição */}
              {item.descricao && (
                <div>
                  <span className="text-sm font-semibold">Descrição:</span>
                  <p className="mt-1 text-muted-foreground whitespace-pre-wrap">{item.descricao}</p>
                </div>
              )}
            </div>
          </div>

          <hr className="my-4" />

          {/* Estoque e datas */}
          <h3 className="text-lg font-semibold mb-4">Estoque e Controle</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 font-semibold">Quantidade Total</th>
                  <th className="text-left p-3 font-semibold">Disponível</th>
                  <th className="text-left p-3 font-semibold">Resgatadas</th>
                  <th className="text-left p-3 font-semibold">Data Criação</th>
                  <th className="text-left p-3 font-semibold">Início Vigência</th>
                  <th className="text-left p-3 font-semibold">Fim Vigência</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-medium">{item.qntd}</td>
                  <td className="p-3">
                    <span className={cn("font-medium", item.qntd_disponivel > 0 ? "text-green-600" : "text-red-600")}>
                      {item.qntd_disponivel}
                    </span>
                  </td>
                  <td className="p-3 font-medium">{item.qntd_resgatada}</td>
                  <td className="p-3">{formatDate(item.date_created)}</td>
                  <td className="p-3">{item.date_start ? formatDate(item.date_start) : '-'}</td>
                  <td className="p-3">{item.date_end ? formatDate(item.date_end) : 'Sem prazo'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
